import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Constants } from '../../../../admin-core/constants';
import { AlertService } from '../../../../admin-core/services';
import { BarcodeScannerLivestreamComponent } from 'ngx-barcode-scanner';

@Component({
  standalone: false,
  selector: 'app-scan-tracking-number',
  // Eager: Angular 22 defaults every component to OnPush, which would leave this view
  // unpainted after its subscribe callbacks (see app.component.ts for the full note).
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './scan-tracking-number.component.html',
  /* `pebbl-dialog.css` replaces the packing-image sheet — that one supplied the
     legacy `.actionBtn` / `.capture-image-btn` / `.add-note-btn` pills this
     template no longer renders, and this one supplies `.btn-primary` /
     `.btn-secondary` and their disabled states. */
  styleUrls: [
    '../../../../shared/components/pebbl-modal/pebbl-dialog.css',
    './scan-tracking-number.component.css',
  ]
})
export class ScanTrackingNumberComponent implements OnInit {
  isLoading: boolean = true;
  @ViewChild('scanner', { static: false })
  scanner: BarcodeScannerLivestreamComponent;
  sampleTrackingNumber: string;

  /**
   * Which symbologies to try.
   *
   * This used to be a bare `type="code_128"`, which is one reader — a label in
   * any other symbology simply never decoded and the dialog sat on "Scanning…"
   * for ever with no way to tell that was what had happened. `mapToReader()`
   * in the library accepts an array, so the readers Quagga actually ships for
   * courier labels are all offered.
   *
   * MUST be a stable reference. `type` is an `@Input` and the library's
   * `ngOnChanges` calls `restart()`, so an inline array literal in the template
   * would be a new object every change-detection pass and restart the camera in
   * a loop.
   */
  // `string[]`, which is what the library's `type` input is declared as —
  // it exports no format union to import.
  readonly scanFormats: string[] = [
    'code_128',   // the Go Logistics / Aus Post consignment default
    'code_39',
    'code_93',
    'i2of5',      // interleaved 2 of 5 — common on freight labels
    'ean',
    'ean_8',
  ];

  /**
   * What the viewfinder is actually doing. The old dialog had only `isLoading`,
   * cleared by a one-second timer whether or not the camera had started — so a
   * failed camera was indistinguishable from a camera that had simply not seen
   * a barcode yet. Both showed "Scanning…" for ever.
   */
  scanState: 'starting' | 'scanning' | 'failed' = 'starting';
  scanError = '';

  /** The typed fallback, for when there is no camera to use. */
  manualEntry = '';
  manualOpen = false;

  constructor( public ngbActiveModal: NgbActiveModal, public alertService: AlertService) { }

  /************************************
    * Life cycle hooks start
  ************************************/

   ngOnInit(): void {}

  ngAfterViewInit() {
    this.startScanner()
  }

  /************************************
   * Life cycle hooks end
   ************************************/

  /************************************
   * Other functions start
  ************************************/

  /**
   * Start the camera and REPORT what happened.
   *
   * `BarcodeScannerLivestreamComponent.start()` is async and rejects when
   * `Quagga.init` fails — the library only `console.log`s the error. It also
   * leaves its own wrapper `[hidden]` until `_started` flips, so a rejected
   * start left this dialog showing an empty viewfinder, "Scanning…" for ever,
   * and a permanently disabled Update. The old code called `start()` without
   * awaiting it or catching it, and cleared its loader on a 1s timer.
   */
  async startScanner() {
    this.scanState = 'starting';
    this.scanError = '';
    this.isLoading = true;
    try {
      await this.scanner.start();
      this.scanState = 'scanning';
    } catch (error: any) {
      this.scanState = 'failed';
      this.scanError = this.cameraMessage(error);
      // No camera means the only way through is typing it.
      this.manualOpen = true;
    }
    this.isLoading = false;
  }

  /** getUserMedia's DOMException names, in words a packer can act on. */
  private cameraMessage(error: any): string {
    switch (error?.name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return 'Camera access was blocked. Allow it for this site, or type the number below.';
      case 'NotFoundError':
      case 'OverconstrainedError':
        return 'No camera was found on this device - type the number below.';
      case 'NotReadableError':
        return 'The camera is in use by another app - close it, or type the number below.';
      default:
        return 'The camera could not be started - type the number below.';
    }
  }

  onValueChanges(result: any) {
    this.sampleTrackingNumber = this.normalise(result?.codeResult?.code);
    this.scanner.stop();
  }

  /** Barcodes and typed numbers both arrive with stray whitespace — a printed
   *  "1800 428 437" is one number, and the server stores it without the gaps. */
  private normalise(value: any): string {
    return (value ?? '').toString().replace(/\s+/g, '').trim();
  }

  scanAgain() {
    this.sampleTrackingNumber = '';
    this.manualEntry = '';
    this.startScanner();
  }

  /** Take the typed number instead of a scan. */
  useManualEntry() {
    const value = this.normalise(this.manualEntry);
    if (!value) {
      return;
    }
    this.sampleTrackingNumber = value;
    // The camera is no longer needed; leaving it running holds the device.
    try {
      this.scanner?.stop();
    } catch {
      /* nothing to stop */
    }
    this.updateTrackingNumber();
  }

  updateTrackingNumber(){
    if(this.sampleTrackingNumber){
      this.alertService.success(Constants.ADD_TRACKING_NUMBER_SUCCESS_MESSAGE);
      this.ngbActiveModal.close(this.sampleTrackingNumber)
      this.sampleTrackingNumber = null;
      try {
        this.scanner?.stop();
      } catch {
        /* already stopped, or never started */
      }
    }
  }

  /************************************
   * Other functions end
   ************************************/

}



